import React, { useContext, useCallback, useEffect } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import ddt from '@wix/da-ddt';
import { BiEventType, BiDataHook, ReferralType } from '../../../biLogger';
import CommentBox from '../../../CommentBox';
import { dateDiff } from '../../../Timestamp/functions';
import { generateReplyEditorId } from '../../utils';
import { useVerification } from '../../../VerificationPopup/withVerification';
import LoadableCommentReduxModule from '../../LoadableCommentReduxModule';
import PlainButtonOrLink from '../../../Buttons/PlainButtonOrLink';
import { CommentContext } from '../CommentContext';
import LikeButton from '../LikeButton';
import AwardBadgeButton from '../AwardBadgeButton';
import { useBiLogger } from '../../../biLogger/hooks/useBiLogger';
import { useFlag } from '../../../redux/flags/hooks';
import { MobileContext } from '../../../Context';
import CommentTimestamp from '../CommentTimestamp';
import sOriginal from './CommentFooter.scss';
import sRedesignB from './CommentFooter.comment_redesign_b.scss';
import sRedesignC from './CommentFooter.comment_redesign_c.scss';
var CommentFooter = function (_a) {
    var _b = _a.canPostComment, canPostComment = _b === void 0 ? true : _b, focusEditor = _a.focusEditor, isLoggedIn = _a.isLoggedIn, disableEdit = _a.disableEdit, restrictLoggedOut = _a.restrictLoggedOut, onReplyPosted = _a.onReplyPosted, replyClassName = _a.replyClassName, className = _a.className, likeButtonClassName = _a.likeButtonClassName, isCommentReduxReady = _a.isCommentReduxReady, currentUser = _a.currentUser, timestampClassName = _a.timestampClassName;
    var _c = useContext(CommentContext), comment = _c.comment, isReplying = _c.isReplying, setEditingState = _c.setEditingState, isCommentVisible = _c.isCommentVisible;
    var isMobile = useContext(MobileContext);
    var commentId = comment.commentId;
    var restrictToVerified = useVerification().restrictToVerified;
    var logBiEvent = useBiLogger();
    var t = useTranslation().t;
    var isRedesignB = useFlag('comment_redesign_b');
    var isRedesignC = useFlag('comment_redesign_c');
    var s = isRedesignB ? sRedesignB : isRedesignC ? sRedesignC : sOriginal;
    var showTimestamp = isRedesignC && !isMobile;
    var onClickEdit = restrictToVerified('comment', function () {
        return setEditingState('editing');
    });
    var onClickReply = restrictToVerified('comment', function () {
        var _a;
        if (!isLoggedIn) {
            restrictLoggedOut({ restrictedActionName: ReferralType.COMMENT });
            return;
        }
        if (!isReplying) {
            // if arrived with reply_to param, we already logged this event in the trigger
            var withBiEvent = !getReplyToParam();
            if (withBiEvent) {
                logBiEvent({
                    evid: BiEventType.CLICK_REPLY_TO_COMMENT,
                    commenterid: (_a = comment.user) === null || _a === void 0 ? void 0 : _a.useridUuid,
                    commentid: comment.commentId,
                    typeid: comment.typeId,
                    itemid: comment.itemId,
                });
            }
        }
        ddt.log('comments', 'Replying on comment, editorId: ', generateReplyEditorId(comment));
        setEditingState(isReplying ? 'none' : 'replying');
        focusEditor(generateReplyEditorId(comment));
    });
    var onCloseEditor = useCallback(function () { return setEditingState('none'); }, [
        setEditingState,
    ]);
    var onReplyDone = useCallback(function (result) {
        setEditingState('none');
        onReplyPosted && result.comment && onReplyPosted(result);
    }, [onReplyPosted, setEditingState]);
    var getReplyToParam = function () {
        var _a;
        if (!window.location.search) {
            return;
        }
        var replyTo = Number((_a = queryString.parse(window.location.search)) === null || _a === void 0 ? void 0 : _a.reply_to);
        return replyTo;
    };
    useEffect(function () {
        if (isReplying) {
            return;
        }
        if (getReplyToParam() === commentId) {
            onClickReply();
        }
    }, [commentId, isReplying, onClickReply]);
    var canReply = !comment.isHidden && !comment.isSpam;
    // Limited to 1 day since post
    var canEdit = !disableEdit &&
        comment.isAuthor &&
        dateDiff(new Date().toISOString(), comment.posted, 'days') === 0;
    if (!isCommentVisible) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(LoadableCommentReduxModule, null),
        React.createElement("div", { className: classnames(s['footer'], className), "data-hook": BiDataHook.COMMENT_FOOTER },
            (canPostComment || !isLoggedIn) && (React.createElement(React.Fragment, null,
                canEdit && (React.createElement(PlainButtonOrLink, { className: classnames(s['footer-item'], s['action']), onClick: onClickEdit, "data-hook": BiDataHook.COMMENT_EDIT_BUTTON, biData: {
                        evid: BiEventType.COMMENT_EDIT_CLICK,
                        commentid: comment.commentId,
                        typeid: comment.typeId,
                        itemid: comment.itemId,
                    }, disabled: !isCommentReduxReady }, t('comment.action.edit'))),
                React.createElement(PlainButtonOrLink, { className: classnames(s['footer-item'], s['action']), onClick: onClickReply, "data-hook": BiDataHook.COMMENT_REPLY_BUTTON, disabled: !isCommentReduxReady }, t('comment.action.reply')))),
            React.createElement("div", { className: classnames(s['footer-item'], s['action']) },
                React.createElement(LikeButton, { className: likeButtonClassName, disabled: !isCommentReduxReady })),
            !comment.isAuthor && (React.createElement("div", { className: classnames(s['footer-item'], s['action']) },
                React.createElement(AwardBadgeButton, { className: likeButtonClassName, disabled: !isCommentReduxReady }))),
            showTimestamp && (React.createElement(CommentTimestamp, { className: classnames(s['footer-item'], s['timestamp'], timestampClassName) }))),
        isReplying && canReply && (React.createElement("div", { className: classnames(s['reply-block'], replyClassName) },
            React.createElement(CommentBox, { onPublishSuccess: onReplyDone, id: generateReplyEditorId(comment), className: s['commentbox-reply'], commentableType: comment.typeId, commentableItemId: comment.itemId, user: currentUser, replyComment: comment, autoFocus: true, onClose: onCloseEditor, canPostComment: !!canPostComment, compact: true })))));
};
CommentFooter.displayName = 'CommentFooter';
export default CommentFooter;
//# sourceMappingURL=CommentFooter.js.map